const MAX_PHOTO_FILE_BYTES = 50 * 1024 * 1024;

const HEIC_MIME_TYPES = new Set([
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence"
]);

const SUPPORTED_PHOTO_MIME_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp"
]);

const PHOTO_MIME_TYPE_BY_EXTENSION: Record<string, string> = {
  heic: "image/heic",
  heif: "image/heif",
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  png: "image/png",
  webp: "image/webp"
};

const HEIC_FILE_BRANDS = new Set(["mif1", "msf1", "heic", "heix", "hevc", "hevx"]);

export const PHOTO_FILE_ACCEPT = ".jpg,.jpeg,.png,.webp,.heic,.heif,image/jpeg,image/png,image/webp,image/heic,image/heif";
export { MAX_PHOTO_FILE_BYTES };

export type PhotoPreparationStage = "checking" | "converting";

export interface PreparedPhotoFile {
  dataURL: string;
  width: number;
  height: number;
  fileName: string;
  mimeType: string;
  byteSize: number;
  convertedFromHeic: boolean;
}

interface PreparePhotoFileOptions {
  onStage?: (stage: PhotoPreparationStage) => void;
}

export async function preparePhotoFile(file: File, options: PreparePhotoFileOptions = {}): Promise<PreparedPhotoFile> {
  options.onStage?.("checking");
  validatePhotoFileSize(file);

  if (isLikelyHeicFile(file) || await hasHeicSignature(file)) {
    return convertHeicPhoto(file, options);
  }

  const mimeType = supportedMimeTypeFor(file);
  if (!mimeType) {
    throw unsupportedPhotoError(file.name);
  }

  try {
    const blob = file.type === mimeType ? file : new Blob([file], { type: mimeType });
    const dataURL = await readBlobAsDataURL(blob);
    const dimensions = await loadImageDimensions(dataURL);
    return {
      dataURL,
      ...dimensions,
      fileName: file.name,
      mimeType,
      byteSize: blob.size,
      convertedFromHeic: false
    };
  } catch {
    throw unsupportedPhotoError(file.name);
  }
}

export function isLikelyHeicFile(file: Pick<File, "name" | "type">) {
  const mimeType = normalizeMimeType(file.type);
  return HEIC_MIME_TYPES.has(mimeType) || ["heic", "heif"].includes(fileExtension(file.name));
}

export async function hasHeicSignature(file: Blob) {
  if (file.size < 12) {
    return false;
  }
  const brand = (await file.slice(8, 12).text()).replace(/\0/g, " ").trim().toLowerCase();
  return HEIC_FILE_BRANDS.has(brand);
}

export function photoFileSizeError(fileName: string, byteSize: number) {
  return `${fileName} is ${formatMegabytes(byteSize)} MB. Photos must be 50 MB or smaller.`;
}

export function photoUploadErrorMessage(caught: unknown, fallback: string) {
  const message = caught instanceof Error ? caught.message : "";
  if (/malware scanner timed out/i.test(message)) {
    return "This photo took too long to save. Try again in a moment.";
  }
  if (/malware scanner|security scan/i.test(message)) {
    return "We couldn’t save this photo right now. Try again in a moment.";
  }
  if (/Photo upload took too long|couldn't reach storage/i.test(message)) {
    return message;
  }
  if (/Failed to fetch|NetworkError|Load failed|fetch failed/i.test(message)) {
    return "Photo upload couldn't reach storage. Check your connection and try again.";
  }
  if (/object storage|presign|asset|upload|not configured/i.test(message)) {
    return "Photo uploads are unavailable in this workspace right now.";
  }
  return message || fallback;
}

async function convertHeicPhoto(file: File, options: PreparePhotoFileOptions): Promise<PreparedPhotoFile> {
  options.onStage?.("converting");
  try {
    const { heicTo } = await import("heic-to/csp");
    const jpeg = await heicTo({ blob: file, type: "image/jpeg", quality: 0.85 });
    const dataURL = await readBlobAsDataURL(jpeg);
    const dimensions = await loadImageDimensions(dataURL);
    return {
      dataURL,
      ...dimensions,
      fileName: jpegFileName(file.name),
      mimeType: "image/jpeg",
      byteSize: jpeg.size,
      convertedFromHeic: true
    };
  } catch {
    throw new Error(`${file.name} couldn't be converted from HEIC. Export it as JPEG and add it again.`);
  }
}

function validatePhotoFileSize(file: File) {
  if (file.size === 0) {
    throw new Error(`${file.name} is empty. Choose another photo.`);
  }
  if (file.size > MAX_PHOTO_FILE_BYTES) {
    throw new Error(photoFileSizeError(file.name, file.size));
  }
}

function supportedMimeTypeFor(file: Pick<File, "name" | "type">) {
  const declaredType = normalizeMimeType(file.type);
  if (SUPPORTED_PHOTO_MIME_TYPES.has(declaredType)) {
    return declaredType;
  }
  const extensionType = PHOTO_MIME_TYPE_BY_EXTENSION[fileExtension(file.name)];
  return extensionType && SUPPORTED_PHOTO_MIME_TYPES.has(extensionType) ? extensionType : undefined;
}

function normalizeMimeType(mimeType: string) {
  const normalized = mimeType.trim().toLowerCase();
  return normalized === "image/jpg" ? "image/jpeg" : normalized;
}

function fileExtension(fileName: string) {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex >= 0 ? fileName.slice(dotIndex + 1).toLowerCase() : "";
}

function jpegFileName(fileName: string) {
  return /\.(?:heic|heif)$/i.test(fileName) ? fileName.replace(/\.(?:heic|heif)$/i, ".jpg") : `${fileName}.jpg`;
}

function unsupportedPhotoError(fileName: string) {
  return new Error(`${fileName} couldn't be added. Use a JPEG, PNG, WebP, HEIC, or HEIF photo up to 50 MB.`);
}

function formatMegabytes(byteSize: number) {
  return (byteSize / (1024 * 1024)).toFixed(1);
}

function readBlobAsDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Unable to read photo."));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(blob);
  });
}

function loadImageDimensions(src: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => reject(new Error("Unable to decode photo."));
    image.src = src;
  });
}
